import { logR2Upload } from "../observability/r2Upload";
import { eventBus } from "../observability/eventBus";
import { makeEvent } from "../observability/eventSchema";
import { useChatStore } from "../state/chatStore";
import { savePendingUpload } from "./pendingUploads";
import type { SessionEvent, SessionSummary } from "../observability/eventSchema";
import type {
  AnswersArtifact,
  LlmExchangeArtifact,
  Manifest,
  ManifestTurn,
  MetricsArtifact,
  TranscriptArtifact,
} from "./types";

type Job =
  | {
      kind: "audio";
      sessionId: string;
      turnIndex: number;
      turnId: string;
      blob: Blob;
      userId: string;
    }
  | {
      kind: "transcript" | "llm" | "metrics";
      sessionId: string;
      turnIndex: number;
      turnId: string;
      body: object;
      userId: string;
    }
  | {
      kind: "manifest";
      method: "POST" | "PUT";
      sessionId: string;
      body: Manifest;
      userId: string;
    }
  | {
      kind: "events";
      sessionId: string;
      userId: string;
      jsonl: string;
    }
  | {
      kind: "summary";
      sessionId: string;
      userId: string;
      body: SessionSummary;
    }
  | {
      kind: "answers";
      sessionId: string;
      userId: string;
      body: AnswersArtifact;
    }
  | {
      // 事前チェックの氏名発話 (spec 2026-07-29 D2)。turn ではないので turns/ に混ぜない
      // (混ぜると admin ダッシュボードの turnCount に幽霊 turn が出る)。
      kind: "preflight-audio";
      sessionId: string;
      userId: string;
      index: number;
      blob: Blob;
    };

const MAX_ATTEMPTS = 3;
const BACKOFFS_MS = [500, 1500];

function pad4(n: number): string {
  return n.toString().padStart(4, "0");
}

function extFromMime(mime: string): string {
  if (mime.startsWith("audio/webm")) return "webm";
  if (mime.startsWith("audio/mp4")) return "mp4";
  if (mime.startsWith("audio/ogg")) return "ogg";
  // continuous mode の発話 audio は wavEncoder が audio/wav で encode する
  if (mime.startsWith("audio/wav")) return "wav";
  return "bin";
}

export interface SessionUploaderOptions {
  fetch?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
}

export class SessionUploader {
  private queue: Job[] = [];
  private running = false;
  private currentUserId: string | null = null;
  private currentManifest: Manifest | null = null;
  private readonly fetchImpl: typeof fetch;
  private readonly sleepImpl: (ms: number) => Promise<void>;

  constructor(opts: SessionUploaderOptions = {}) {
    // fetch は global (Window) に束縛されている必要がある。`this.fetchImpl = fetch` として
    // `this.fetchImpl(...)` と「オブジェクトのメソッド」形で呼ぶと this が Window でなくなり、
    // ブラウザは "Failed to execute 'fetch' on 'Window': Illegal invocation" を投げて
    // 全 upload が 3 回リトライして全滅する (2026-07-21 実機で判明。opts.fetch を注入する
    // 単体テストと、エンドポイント直叩きの synthetic smoke では踏まなかった盲点)。
    // 素の fetch をラップして常に free-function 呼び出しにし、this 依存を断つ。
    this.fetchImpl = opts.fetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
    this.sleepImpl = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
  }

  async startSession(args: {
    userId: string;
    sessionId: string;
    surveyId: string;
    userAgent: string;
    startedAt: string;
    displayName?: string;
  }): Promise<void> {
    this.currentUserId = args.userId;
    const manifest: Manifest = {
      schemaVersion: 1,
      userId: args.userId,
      sessionId: args.sessionId,
      surveyId: args.surveyId,
      startedAt: args.startedAt,
      status: "in_progress",
      userAgent: args.userAgent,
      ...(args.displayName ? { displayName: args.displayName } : {}),
      turns: [],
    };
    this.currentManifest = manifest;
    this.enqueue({
      kind: "manifest",
      method: "POST",
      sessionId: args.sessionId,
      body: { ...manifest, turns: [...manifest.turns] },
      userId: args.userId,
    });
  }

  enqueueAudio(args: { sessionId: string; turnIndex: number; turnId: string; blob: Blob }): void {
    if (!this.currentUserId) return;
    this.enqueue({
      kind: "audio",
      sessionId: args.sessionId,
      turnIndex: args.turnIndex,
      turnId: args.turnId,
      blob: args.blob,
      userId: this.currentUserId,
    });
  }

  // 事前チェックの氏名発話 (spec 2026-07-29 D6)。**userId を引数で受ける**のがこのメソッドの
  // 存在理由 — startSession を呼ぶのは本編突入時の ChatScreen なので、事前チェックの最中は
  // currentUserId がまだ null で、既存 enqueueAudio はその early return で黙って捨ててしまう。
  enqueuePreflightAudio(args: {
    userId: string;
    sessionId: string;
    index: number;
    blob: Blob;
  }): void {
    this.enqueue({
      kind: "preflight-audio",
      sessionId: args.sessionId,
      userId: args.userId,
      index: args.index,
      blob: args.blob,
    });
  }

  enqueueTranscript(payload: TranscriptArtifact & { sessionId: string; turnIndex: number }): void {
    if (!this.currentUserId) return;
    const { sessionId, turnIndex, ...artifact } = payload;
    this.enqueue({
      kind: "transcript",
      sessionId,
      turnIndex,
      turnId: artifact.turnId,
      body: artifact,
      userId: this.currentUserId,
    });
  }

  enqueueLlm(payload: LlmExchangeArtifact & { sessionId: string; turnIndex: number }): void {
    if (!this.currentUserId) return;
    const { sessionId, turnIndex, ...artifact } = payload;
    this.enqueue({
      kind: "llm",
      sessionId,
      turnIndex,
      turnId: artifact.turnId,
      body: artifact,
      userId: this.currentUserId,
    });
  }

  enqueueMetrics(payload: MetricsArtifact & { sessionId: string; turnIndex: number }): void {
    if (!this.currentUserId) return;
    const { sessionId, turnIndex, ...artifact } = payload;
    this.enqueue({
      kind: "metrics",
      sessionId,
      turnIndex,
      turnId: artifact.turnId,
      body: artifact,
      userId: this.currentUserId,
    });
  }

  enqueueEvents(args: { sessionId: string; events: SessionEvent[] }): void {
    if (!this.currentUserId) return;
    if (args.events.length === 0) return;
    this.enqueue({
      kind: "events",
      sessionId: args.sessionId,
      userId: this.currentUserId,
      jsonl: args.events.map((e) => JSON.stringify(e)).join("\n"),
    });
  }

  enqueueSessionSummary(args: { sessionId: string; summary: SessionSummary }): void {
    if (!this.currentUserId) return;
    this.enqueue({
      kind: "summary",
      sessionId: args.sessionId,
      userId: this.currentUserId,
      body: args.summary,
    });
  }

  /**
   * 確定回答の一覧を同一キーへ冪等に全上書きする (spec 2026-08-02)。回答の確定・✏️ 訂正・
   * 送信の 3 契機から呼ばれるので、他の enqueue* と同じく **await させない** —
   * 呼び出し側 (handleSubmit / onEditText) は保存の成否を待たない (原則 5)。
   */
  enqueueAnswers(args: { sessionId: string; artifact: AnswersArtifact }): void {
    if (!this.currentUserId) return;
    this.enqueue({
      kind: "answers",
      sessionId: args.sessionId,
      userId: this.currentUserId,
      body: args.artifact,
    });
  }

  appendTurnToManifest(turn: ManifestTurn & { sessionId: string }): void {
    if (!this.currentManifest) return;
    const { sessionId: _sid, ...t } = turn;
    void _sid;
    this.currentManifest.turns = [
      ...this.currentManifest.turns.filter((x) => x.turnId !== t.turnId),
      t,
    ];
  }

  async finalizeManifest(args: {
    sessionId: string;
    status: "completed" | "abandoned";
    endedAt: string;
  }): Promise<void> {
    if (!this.currentManifest || !this.currentUserId) return;
    const final: Manifest = {
      ...this.currentManifest,
      status: args.status,
      endedAt: args.endedAt,
    };
    this.currentManifest = final;
    this.enqueue({
      kind: "manifest",
      method: "PUT",
      sessionId: args.sessionId,
      body: { ...final, turns: [...final.turns] },
      userId: this.currentUserId,
    });
    await this.flush();
  }

  async flush(): Promise<void> {
    while (this.running) {
      await this.sleepImpl(10);
    }
    if (this.queue.length === 0) return;
    this.running = true;
    try {
      while (this.queue.length > 0) {
        const job = this.queue.shift()!;
        await this.dispatch(job);
      }
    } finally {
      this.running = false;
    }
  }

  private enqueue(job: Job): void {
    this.queue.push(job);
    void this.flush();
  }

  private async dispatch(job: Job): Promise<void> {
    const { url, init } = this.buildRequest(job);
    const startedAt = new Date().toISOString();
    logR2Upload({
      kind: job.kind,
      phase: "started",
      sessionId: job.sessionId,
      turnId: "turnId" in job ? job.turnId : undefined,
      recordedAt: startedAt,
    });
    const t0 = Date.now();
    let lastErr: unknown = null;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
      try {
        const res = await this.fetchImpl(url, init);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        logR2Upload({
          kind: job.kind,
          phase: "succeeded",
          sessionId: job.sessionId,
          turnId: "turnId" in job ? job.turnId : undefined,
          recordedAt: new Date().toISOString(),
          latencyMs: Date.now() - t0,
        });
        return;
      } catch (err) {
        lastErr = err;
        if (attempt < MAX_ATTEMPTS) {
          await this.sleepImpl(BACKOFFS_MS[attempt - 1]);
        }
      }
    }
    const errorText = lastErr instanceof Error ? lastErr.message : String(lastErr);
    logR2Upload({
      kind: job.kind,
      phase: "failed",
      sessionId: job.sessionId,
      turnId: "turnId" in job ? job.turnId : undefined,
      recordedAt: new Date().toISOString(),
      attempts: MAX_ATTEMPTS,
      error: errorText,
    });

    // spec 2026-07-29 §3.1.3。logR2Upload は localStorage の dev overlay 用
    // buffer にしか残らない (前提 spec §8.3) ため、R2 保存が壊れている
    // セッションほど証拠が残らないという逆説があった。events.jsonl にも残す。
    // この emit 自体が失敗を呼ぶループは buffer 側の snapshot 除外と
    // 件数上限で断ってある (§3.3)。
    try {
      const sessionId = useChatStore.getState().sessionId;
      if (sessionId) {
        eventBus.emit(
          makeEvent("storage.upload_failed", sessionId, {
            kind: job.kind,
            attempts: MAX_ATTEMPTS,
            error: errorText.slice(0, 200),
          })
        );
      }
    } catch {
      /* 観測のために保存経路を壊さない (原則 5) */
    }

    // 送信できなかった events だけを退避して次回セッションで送り直す (§3.4.2)。
    // audio は Blob で localStorage に入らず、manifest/summary は §9.4 の
    // 限界として受け入れる。events だけが失われると復元できない。
    if (job.kind === "events") {
      savePendingUpload({
        sessionId: job.sessionId,
        userId: job.userId,
        jsonl: job.jsonl,
        failedAt: new Date().toISOString(),
      });
    }
  }

  private buildRequest(job: Job): { url: string; init: RequestInit } {
    if (job.kind === "manifest") {
      return {
        url: `/api/sessions/${job.sessionId}/manifest`,
        init: {
          method: job.method,
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ userId: job.userId, manifest: job.body }),
        },
      };
    }
    if (job.kind === "events") {
      return {
        url: `/api/sessions/${job.sessionId}/events?userId=${encodeURIComponent(job.userId)}`,
        init: {
          method: "PUT",
          headers: {
            "content-type": "application/x-ndjson",
          },
          body: job.jsonl,
        },
      };
    }
    if (job.kind === "summary") {
      return {
        url: `/api/sessions/${job.sessionId}/summary`,
        init: {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ userId: job.userId, artifact: job.body }),
        },
      };
    }
    if (job.kind === "answers") {
      return {
        url: `/api/sessions/${job.sessionId}/answers`,
        init: {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ userId: job.userId, artifact: job.body }),
        },
      };
    }
    if (job.kind === "preflight-audio") {
      const form = new FormData();
      form.append("userId", job.userId);
      const ext = extFromMime(job.blob.type);
      form.append("audio", job.blob, `audio.${ext}`);
      form.append("contentType", job.blob.type || "application/octet-stream");
      return {
        url: `/api/sessions/${job.sessionId}/preflight/${job.index}/audio`,
        init: { method: "POST", body: form },
      };
    }
    // turn を持たない job (manifest / events / summary / answers / preflight-audio) は
    // 必ずこの行より前で return する。以降は job.turnIndex 前提で pad4(undefined) が
    // 同期 throw し、buildRequest の呼び出しは dispatch の try の外なので flush を貫通して
    // enqueue の `void this.flush()` で unhandled rejection になる。
    const turnPath = `${pad4(job.turnIndex)}/${job.turnId}`;
    if (job.kind === "audio") {
      const form = new FormData();
      form.append("userId", job.userId);
      const ext = extFromMime(job.blob.type);
      form.append("audio", job.blob, `audio.${ext}`);
      form.append("contentType", job.blob.type || "application/octet-stream");
      return {
        url: `/api/sessions/${job.sessionId}/turns/${turnPath}/audio`,
        init: { method: "POST", body: form },
      };
    }
    return {
      url: `/api/sessions/${job.sessionId}/turns/${turnPath}/${job.kind}`,
      init: {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ userId: job.userId, artifact: job.body }),
      },
    };
  }
}
